import { MethodType, PageDirection, Resource, ResourceList, getHateoasElement } from "@myRedux/networkState";
import { createAppAsyncThunk } from "@myRedux/createAppAsyncThunk";
import { updateUrlParamEntry } from "app/urlUtility";
import { getErrorMessage } from "app/error";
import { RootState } from "@myRedux/store";

export interface SearchArgument<T extends Resource> {
  resource: T,
  searchText: string,
  pageNumber?: number 
}

export interface PagingArgument<T extends Resource> {
  pageDirection: PageDirection,
  resourceList: ResourceList<T>
}

export interface PostArgument<T> {
  resourceWithPostRel: Resource,
  resourceToPost: T
}

export interface PutArgument<T> {
  resourceWithPutRel: Resource,
  resourceToPut: T
}

function getPageNumberFromSearchArgument(searchArgument: SearchArgument<Resource>) {
  if (searchArgument.pageNumber) {
    return searchArgument.pageNumber.toString();
  }
  return '1';
}

export const getResourceFromRel = <T extends Resource>(
  typePrefix: string, 
  rel: string) => {
  return createAppAsyncThunk(
    typePrefix,
    async (resource: T, thunkAPI) => {
      try {
        const relHateoasElement = getHateoasElement(resource, rel);
        if (relHateoasElement) {
          const response = await thunkAPI.extra.apiClient.get(relHateoasElement.href);
          if (response.hasError()) {
            return thunkAPI.rejectWithValue(response.statusText);
          }
          return response.data;
        }
        return thunkAPI.rejectWithValue(`No "${rel}" HATEOAS link`);
      }
      catch (error) {
        return thunkAPI.rejectWithValue(getErrorMessage(error));
      }
    },
  );
}

export const getResourceFromResourceFuncRel = <T extends Resource>(
  typePrefix: string,
  rel: string,
  getResourceFromStateFunc: (state: RootState) => T) => {
  return createAppAsyncThunk(
    typePrefix,
    async (_, thunkAPI) => {
      try {
        const resource = getResourceFromStateFunc(thunkAPI.getState());
        const relHateoasElement = getHateoasElement(resource, rel);
        if (relHateoasElement) {
          const response = await thunkAPI.extra.apiClient.get(relHateoasElement.href);
          if (response.hasError()) {
            return thunkAPI.rejectWithValue(response.statusText);
          }
          return response.data;
        }
        return thunkAPI.rejectWithValue(`No "${rel}" HATEOAS link`);
      }
      catch (error) {
        return thunkAPI.rejectWithValue(getErrorMessage(error));
      }
    },
  );
}

export const searchAsyncThunk = <T extends Resource>(typePrefix: string, rel: string, searchParameter: string, pageParameter?: string) => {
  return createAppAsyncThunk(
    typePrefix,
    async (arg: SearchArgument<T>, thunkAPI) => {
      try {
        const relHateoasElement = getHateoasElement(arg.resource, rel);
        if (relHateoasElement) {
          const urlParts = relHateoasElement.href.split('?');
          const urlParams = new URLSearchParams(urlParts[1]);
          updateUrlParamEntry(searchParameter, arg.searchText, urlParams);
          if (pageParameter) {
            updateUrlParamEntry(pageParameter, getPageNumberFromSearchArgument(arg), urlParams);
          }
          const reconstructedUrl = urlParts[0] + '?' + urlParams.toString();
          const response = await thunkAPI.extra.apiClient.get(reconstructedUrl);
          if (response.hasError()) {
            return thunkAPI.rejectWithValue(response.statusText);
          }
          return response.data;
        }
        return thunkAPI.rejectWithValue('No "school_districts" HATEOAS link');
      }
      catch (error) {
        return thunkAPI.rejectWithValue(getErrorMessage(error));
      }
    },
  );
}

export const pagingAsyncThunk = <T extends Resource>(typePrefix: string) => {
  return createAppAsyncThunk(
    typePrefix,
    async (arg: PagingArgument<T>, thunkAPI) => {
      try {
        const pagingHateoasElement = getHateoasElement(arg.resourceList, arg.pageDirection);
        if (pagingHateoasElement) {
          const response = await thunkAPI.extra.apiClient.get(pagingHateoasElement.href);
          if (response.hasError()) {
            return thunkAPI.rejectWithValue(response.statusText);
          }
          return response.data;
        }
        return thunkAPI.rejectWithValue(`No "${arg.pageDirection}" HATEOAS link`);
      }
      catch (error) {
        return thunkAPI.rejectWithValue(getErrorMessage(error));
      }
    },
  );
}

export const urlResourceCallAsyncThunk = <T extends Resource>(typePrefix: string, path: string) => {
  return createAppAsyncThunk(
    typePrefix,
    async (_, thunkAPI) => {
      try {
        const response = await thunkAPI.extra.apiClient.get(path)
        if (response.hasError()) {
          return thunkAPI.rejectWithValue(response.statusText);
        }
        return response.data as T;
      }
      catch (error) {
        return thunkAPI.rejectWithValue(getErrorMessage(error));
      }
    },
  );
}

export const windowUrlResourceCallAsyncThunk = <T extends Resource>(typePrefix: string) => {
  return createAppAsyncThunk(
    typePrefix,
    async (_, thunkAPI) => {
      try {
        const response = await thunkAPI.extra.apiClient.get(window.location.pathname);
        if (response.hasError()) {
          return thunkAPI.rejectWithValue(response.statusText);
        }
        return response.data as T;
      }
      catch (error) {
        return thunkAPI.rejectWithValue(getErrorMessage(error));
      }
    },
  );
}

export const postResourceFromRel = <T extends Resource>(
  typePrefix: string, 
  rel: string,
  useAuthClient: boolean = false) => {
  return createAppAsyncThunk(
    typePrefix,
    async (arg: PostArgument<T>, thunkAPI) => {
      try {
        const apiClient = useAuthClient ? thunkAPI.extra.authClient : thunkAPI.extra.apiClient;
        const relHateoasElement = getHateoasElement(arg.resourceWithPostRel, rel, MethodType.POST);
        if (relHateoasElement) {
          const response = await apiClient.post(relHateoasElement.href, arg.resourceToPost);
          if (response.hasError()) {
            return thunkAPI.rejectWithValue(response.statusText);
          }
          return response.data;
        }
        return thunkAPI.rejectWithValue(`No "${rel}" HATEOAS link`);
      }
      catch (error) {
        return thunkAPI.rejectWithValue(getErrorMessage(error));
      }
    },
  );
}

export const putResourceFromRel = <T extends Resource>(
  typePrefix: string, 
  rel: string) => {
  return createAppAsyncThunk(
    typePrefix,
    async (arg: PutArgument<T>, thunkAPI) => {
      try {
        const relHateoasElement = getHateoasElement(arg.resourceWithPutRel, rel, MethodType.PUT);
        if (relHateoasElement) {
          const response = await thunkAPI.extra.apiClient.put(relHateoasElement.href, arg.resourceToPut);
          if (response.hasError()) {
            return thunkAPI.rejectWithValue(response.statusText);
          }
          return arg.resourceToPut;
        }
        return thunkAPI.rejectWithValue(`No "${rel}" HATEOAS link`);
      }
      catch (error) {
        return thunkAPI.rejectWithValue(getErrorMessage(error));
      }
    },
  );
}